import { useEffect, useRef, useState } from 'react'
import type { AgentAvatar } from '@algoboo/schema/api'
import { describeAvatar } from './catalog.js'
import { fitAvatar, fittedAvatarKey } from './fit.js'
import type * as RendererModule from './three/renderer.js'
import type { AgentView } from './three/renderer.js'

export interface SoftAgentProps {
  avatar: AgentAvatar
  size?: number
  /** `portrait`: a bust for chips and rows (≤ 64 px). `full`: the whole character for cards, heroes and pickers. */
  framing?: 'portrait' | 'full'
  title?: string
  className?: string
  /** Live geometry with orbit controls (the customizer). */
  interactive?: boolean
  /**
   * The focused mascot on a page (profile hero): shown as its bitmap first, then brought to life (breathing, blinking,
   * glancing toward the pointer) once the page is idle. Stays a still image under prefers-reduced-motion.
   */
  live?: boolean
}

type Renderer = typeof RendererModule
let rendererModule: Renderer | undefined
const loadRenderer = () => import('./three/renderer.js').then((m) => (rendererModule = m))
const hasWebGL = () => typeof WebGL2RenderingContext !== 'undefined'
const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/**
 * An agent's mascot. Lists get cached bitmaps (one shared offscreen renderer draws each look once per size bucket;
 * Cache Storage keeps them across visits); only the focused mascot runs live. Until a bitmap exists the slot shows a
 * quiet ghost silhouette of the same framing, so nothing jumps when it arrives.
 */
export function SoftAgent({ avatar, size = 48, framing = 'portrait', title, className, interactive = false, live = false }: SoftAgentProps) {
  const label = title ?? describeAvatar(avatar)
  const key = fittedAvatarKey(avatar)
  const portraitKey = interactive ? '' : `${framing}:${size}:${key}`
  const [portrait, setPortrait] = useState<{ key: string; url: string; instant: boolean } | undefined>(() => {
    if (interactive || !rendererModule) return undefined
    const url = rendererModule.peekPortrait(fitAvatar(avatar), framing, size)
    return url ? { key: `${framing}:${size}:${key}`, url, instant: true } : undefined
  })
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const [liveReady, setLiveReady] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const hostRef = useRef<HTMLDivElement>(null)
  const view = useRef<AgentView | null>(null)
  const latest = useRef(fitAvatar(avatar))
  latest.current = fitAvatar(avatar)
  const showPortrait = portrait?.key === portraitKey
  const wantsLive = interactive || (live && !reducedMotion())

  // bitmap (everything but the customizer)
  useEffect(() => {
    if (interactive) return
    if (!hasWebGL()) { setFailed(true); return }
    setFailed(false)
    const cached = rendererModule?.peekPortrait(latest.current, framing, size)
    if (cached) { setPortrait((p) => (p?.key === portraitKey ? p : { key: portraitKey, url: cached, instant: true })); return }
    let cancelled = false
    const abort = new AbortController()
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      observer.disconnect()
      const snapshot = latest.current
      const requested = portraitKey
      void loadRenderer().then((m) => m.renderPortrait(snapshot, abort.signal, framing, m.CAMERA.yaw, size)).then((url) => {
        if (!cancelled && url) setPortrait({ key: requested, url, instant: false })
      }).catch(() => { if (!cancelled) setFailed(true) })
    }, { rootMargin: '200px' })
    if (hostRef.current) observer.observe(hostRef.current)
    return () => { cancelled = true; abort.abort(); observer.disconnect() }
  }, [interactive, portraitKey, framing, size, attempt])

  // live canvas (customizer, and the focused mascot once its bitmap is up)
  const liveGate = interactive || (wantsLive && showPortrait)
  useEffect(() => {
    if (!liveGate || !wantsLive) return
    if (!hasWebGL()) { setFailed(true); return }
    setFailed(false)
    let cancelled = false
    let idle = 0
    const start = () => loadRenderer().then(async ({ mountAgentView, prepareAgentAssets }) => {
      let family: AgentAvatar['family']
      do { family = latest.current.family; await prepareAgentAssets(latest.current) } while (!cancelled && latest.current.family !== family)
      if (cancelled || !canvasRef.current) return
      view.current = mountAgentView(canvasRef.current, latest.current, { orbit: interactive, framing: interactive ? 'full' : framing, onFirstFrame: () => { if (!cancelled) setLiveReady(true) }, onError: () => { if (!cancelled) { setFailed(true); setLiveReady(false) } } })
    }).catch(() => { if (!cancelled && interactive) setFailed(true) })
    const ric = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback
    if (interactive || !ric) void start()
    else idle = ric(() => void start(), { timeout: 1500 })
    return () => {
      cancelled = true
      if (idle) (globalThis as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback?.(idle)
      view.current?.dispose(); view.current = null; setLiveReady(false)
    }
  }, [liveGate, wantsLive, interactive, framing, attempt])

  useEffect(() => {
    if (view.current) { setFailed(false); view.current.update(latest.current) }
  }, [key]) // config, not object identity; keep initial loading failures visible

  const state = failed ? 'unavailable' : interactive ? (liveReady ? 'ready' : 'loading') : showPortrait ? 'ready' : 'loading'
  return (
    <div ref={hostRef} role={label && !interactive ? 'img' : undefined} aria-label={label || undefined} aria-hidden={!label && !interactive ? true : undefined}
      className={className} data-agent-renderer={interactive ? 'webgl' : '3d-portrait'} data-render-state={state} data-live={wantsLive && liveReady ? '' : undefined}
      style={{ width: size, height: size, aspectRatio: '1 / 1', position: 'relative', display: 'block', flexShrink: 0, maxWidth: '100%', filter: className?.includes('rh-mascot') ? undefined : 'var(--avatar-filter)' }}>
      {!failed && (interactive ? !liveReady : !showPortrait) && <Ghost framing={interactive ? 'full' : framing} />}
      {showPortrait && <img src={portrait.url} alt="" width={size} height={size} draggable={false} decoding="async"
        onLoad={portrait.instant || reducedMotion() ? undefined : (event) => { event.currentTarget.animate?.([{ opacity: 0, transform: 'scale(0.985)' }, { opacity: 1, transform: 'none' }], { duration: 180, easing: 'ease-out' }) }}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', opacity: wantsLive && liveReady ? 0 : 1 }} />}
      {wantsLive && <canvas ref={canvasRef} role={interactive ? 'img' : undefined} aria-label={interactive ? label || '3D agent preview' : undefined} aria-hidden={interactive ? undefined : true}
        tabIndex={interactive ? 0 : undefined}
        onKeyDown={interactive ? (event) => {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); view.current?.rotate(event.key === 'ArrowLeft' ? -0.2 : 0.2) }
          else if (event.key === 'Home') { event.preventDefault(); view.current?.reset() }
          else if (event.key === '+' || event.key === '=') { event.preventDefault(); view.current?.zoom(0.1) }
          else if (event.key === '-') { event.preventDefault(); view.current?.zoom(-0.1) }
        } : undefined}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', cursor: interactive ? 'grab' : undefined, touchAction: interactive ? 'pan-y' : undefined, opacity: liveReady && !failed ? 1 : 0, transition: 'opacity 160ms ease-out' }} />}
      {interactive && !failed && <button type="button" aria-label="Reset 3D view" onClick={() => view.current?.reset()}
        className="absolute bottom-2 right-2 rounded-full border border-line bg-surface/80 px-2.5 py-1 text-[11px] font-medium text-ink-3 backdrop-blur-sm hover:text-ink">Reset view</button>}
      {failed && <span style={{ position: 'absolute', inset: 0, display: 'grid', placeContent: 'center', gap: 8, textAlign: 'center', fontSize: size > 100 ? 12 : 8, color: 'var(--ink-3)', padding: 6 }}>
        {size > 100 ? 'Preview unavailable' : '3D'}
        {size > 100 && <button type="button" onClick={() => { setFailed(false); setAttempt((n) => n + 1) }}>Retry preview</button>}
      </span>}
    </div>
  )
}

/** The brand ghost in the theme's hairline colour, placed as the mascot will be: holds the slot without a jump. */
function Ghost({ framing }: { framing: 'portrait' | 'full' }) {
  const box = framing === 'portrait' ? { left: '17%', top: '12%', width: '66%' } : { left: '30%', top: '28%', width: '40%' }
  return (
    <svg aria-hidden="true" viewBox="8 12 48 46" style={{ position: 'absolute', ...box, height: 'auto', color: 'var(--ink)', opacity: 0.07 }}>
      <path d="M12 50C9 34 16 14 32 14s23 20 20 36c-1 5-6 5-9 4-2 2-6 2-8 0-2 2-6 2-8 0-3 1-8 1-9-4Z" fill="currentColor" />
    </svg>
  )
}
